// =============================================================================
// Orçamento × área — o orçado em análise (Planilha orçamentária, 071)
//
// Item 3.2 do pedido do Financeiro de 20/10, com o comentário da Jéssica:
//   · todas as áreas, cada uma com o status; abre uma por uma e vê, lado a
//     lado, o realizado do ano base, o pedido do gestor e o aprovado;
//   · aprova como veio, aprova com outro valor (justificando) ou devolve a
//     linha (justificando). O aprovado vale na hora: entra na planilha oficial
//     e substitui o que havia naquela área e categoria;
//   · a visão consolidada soma todas as áreas, para ver o impacto no total.
//
// A planilha oficial (aba Planilha) só recebe o que foi aprovado aqui.
// =============================================================================

import { Fragment, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buscarAreasProposta, buscarLinhasPropostas, buscarProposta, decidirProposta, detalheDaLinha, FalhaApi,
  type AreaProposta, type DecisaoProposta, type LinhaProposta,
} from "../lib/api.ts";
import { moeda, percentual } from "../lib/formato.ts";
import {
  COR_STATUS_AREA, MESES, ROTULO_SITUACAO, ROTULO_STATUS_AREA, soma, textoMesesAcima, variacao,
} from "../lib/proposta.ts";
import { Painel, Vazio } from "../componentes/ui.tsx";
import { CaixaGrade, CLASSE_BOTAO_BARRA } from "../componentes/Grade.tsx";
import { Celula, curto } from "./PropostaGestor.tsx";

const reais = (v: bigint | string) => moeda(typeof v === "bigint" ? v.toString() : v);
function Variacao({ base, pedido }: { base: bigint; pedido: bigint }) {
  const v = variacao(base, pedido);
  if (v === null) return <span className="text-tinta-fraca">{pedido > 0n ? "novo" : "—"}</span>;
  return <span className={v > 0 ? "text-atencao-tinta" : "text-ok-tinta"}>{v > 0 ? "+" : ""}{percentual(v)}</span>;
}

export function AnaliseOrcamento() {
  const areasQ = useQuery({ queryKey: ["propostas-areas"], queryFn: buscarAreasProposta });
  const ano = useMemo(() => Math.max(0, ...(areasQ.data?.areas ?? []).map((a) => a.exercicio_ano)), [areasQ.data]);
  const areas = useMemo(() => (areasQ.data?.areas ?? []).filter((a) => a.exercicio_ano === ano), [areasQ.data, ano]);
  const [vista, setVista] = useState<"areas" | "consolidado">("areas");
  const [aberta, setAberta] = useState<string | null>(null);

  if (areasQ.isPending) return <div className="pagina py-6 text-[13px] text-tinta-fraca">Carregando…</div>;
  if (areasQ.isError) return <div className="pagina py-6"><Vazio titulo="Não foi possível carregar" dica={(areasQ.error as FalhaApi).message} /></div>;
  if (!ano) {
    return <div className="pagina py-6"><Vazio titulo="Nenhum exercício em montagem" dica="Abra o exercício do próximo ano para as áreas montarem o orçamento." /></div>;
  }

  const t = (k: "base_centavos" | "pedido_centavos" | "aprovado_centavos") => areas.reduce((s, a) => s + BigInt(a[k]), 0n);
  const contagem = (st: AreaProposta["status"][]) => areas.filter((a) => st.includes(a.status)).length;
  const areaAberta = areas.find((a) => a.centro_custo_id === aberta);

  return (
    <div className="pagina pb-20">
      <header className="pt-7 pb-4">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">Orçamento {ano} · em análise</p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">Orçamento × área</h1>
        <p className="mt-1 max-w-3xl text-[14px] text-tinta-fraca">
          O que cada área pediu para {ano}, contra o que gastou em {ano - 1}. O que você aprova entra na hora na planilha
          oficial; o que devolve volta para o gestor.
        </p>
      </header>

      <div className="mb-4 grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3 text-[12.5px]">
        <Resumo rotulo="Para analisar" valor={String(contagem(["em_analise"]))} destaque={contagem(["em_analise"]) > 0} />
        <Resumo rotulo="Com o gestor" valor={String(contagem(["nao_iniciada", "rascunho", "devolvida"]))} apoio="não enviadas ou devolvidas" />
        <Resumo rotulo="Aprovadas" valor={String(contagem(["aprovada", "aprovada_com_ajustes"]))} apoio={`de ${areas.length} áreas`} />
        <Resumo rotulo={`Gasto em ${ano - 1}`} valor={reais(t("base_centavos"))} apoio="realizado e média, das propostas" />
        <Resumo rotulo={`Pedido para ${ano}`} valor={reais(t("pedido_centavos"))}
          apoio={<Variacao base={t("base_centavos")} pedido={t("pedido_centavos")} />} />
        <Resumo rotulo="Aprovado" valor={reais(t("aprovado_centavos"))} apoio="já na planilha oficial" />
      </div>

      <div className="mb-3 flex gap-1" role="tablist" aria-label="Visão">
        {(["areas", "consolidado"] as const).map((v) => (
          <button key={v} type="button" role="tab" aria-selected={vista === v} onClick={() => setVista(v)}
            className={`rounded-lg px-3 py-1.5 text-[12.5px] font-semibold ${vista === v ? "bg-azul text-white" : "bg-superficie-2 text-tinta-media"}`}>
            {v === "areas" ? "Por área" : "Consolidado"}
          </button>
        ))}
      </div>

      {vista === "areas" ? (
        <>
          <CaixaGrade>
            <table className="w-full border-separate border-spacing-0 text-[12.5px]">
              <thead className="sticky top-0 z-10 bg-superficie-2 text-left text-[11px] font-semibold tracking-wide text-tinta-fraca">
                <tr>
                  {["Área", "Status", "Linhas", `${ano - 1}`, `Pedido ${ano}`, "vs", "Aprovado", "Enviada em"].map((h, i) => (
                    <th key={h} className={`border-b border-linha px-3 py-2 ${i >= 3 && i <= 6 ? "text-right" : ""}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {areas.map((a) => (
                  <tr key={a.centro_custo_id} onClick={() => setAberta(a.proposta_id ? (aberta === a.centro_custo_id ? null : a.centro_custo_id) : null)}
                    className={`${a.proposta_id ? "cursor-pointer hover:bg-superficie-2/60" : ""} ${aberta === a.centro_custo_id ? "bg-superficie-2" : ""}`}>
                    <td className="border-b border-linha px-3 py-2 font-semibold">{a.centro_custo_codigo} · {a.centro_custo_nome}</td>
                    <td className="border-b border-linha px-3 py-2">
                      <span className={`rounded px-1.5 py-0.5 text-[11.5px] font-semibold ${COR_STATUS_AREA[a.status]}`}>{ROTULO_STATUS_AREA[a.status]}</span>
                    </td>
                    <td className="border-b border-linha px-3 py-2 text-tinta-media">
                      {a.linhas ? `${a.aprovadas}/${a.linhas} aprovadas${a.enviadas ? ` · ${a.enviadas} para analisar` : ""}${a.devolvidas ? ` · ${a.devolvidas} devolvidas` : ""}` : "—"}
                    </td>
                    <td className="num border-b border-linha px-3 py-2 text-right">{reais(a.base_centavos)}</td>
                    <td className="num border-b border-linha px-3 py-2 text-right font-semibold">{reais(a.pedido_centavos)}</td>
                    <td className="num border-b border-linha px-3 py-2 text-right"><Variacao base={BigInt(a.base_centavos)} pedido={BigInt(a.pedido_centavos)} /></td>
                    <td className="num border-b border-linha px-3 py-2 text-right">{reais(a.aprovado_centavos)}</td>
                    <td className="border-b border-linha px-3 py-2 text-tinta-fraca">{a.enviada_em ? new Date(a.enviada_em).toLocaleDateString("pt-BR") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CaixaGrade>
          {areaAberta?.proposta_id && <DetalheArea key={areaAberta.proposta_id} area={areaAberta} ano={ano} />}
        </>
      ) : (
        <Consolidado ano={ano} codigoDe={new Map(areas.map((a) => [a.centro_custo_id, a.centro_custo_codigo]))} />
      )}
    </div>
  );
}

function Resumo({ rotulo, valor, apoio, destaque }: { rotulo: string; valor: string; apoio?: ReactNode; destaque?: boolean }) {
  return (
    <div className={`rounded-xl border bg-superficie px-4 py-3 ${destaque ? "border-laranja" : "border-linha"}`}>
      <p className="text-[10.5px] font-semibold tracking-[0.06em] text-tinta-fraca uppercase">{rotulo}</p>
      <p className="num mt-0.5 text-[17px] font-bold">{valor}</p>
      {apoio && <p className="text-[11.5px] text-tinta-fraca">{apoio}</p>}
    </div>
  );
}

type Edicao = { linha: LinhaProposta; modo: "ajustar" | "devolver"; aprovado: string[]; justificativa: string };

function DetalheArea({ area, ano }: { area: AreaProposta; ano: number }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["proposta", area.proposta_id], queryFn: () => buscarProposta(area.proposta_id!) });
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const [edicao, setEdicao] = useState<Edicao | null>(null);
  const decidir = useMutation({
    mutationFn: (d: DecisaoProposta[]) => decidirProposta(area.proposta_id!, d),
    onSuccess: () => {
      setEdicao(null);
      for (const k of [["proposta", area.proposta_id], ["propostas-areas"], ["propostas-linhas"], ["planilha"]]) void qc.invalidateQueries({ queryKey: k });
    },
  });
  if (q.isPending) return <p className="mt-4 text-[13px] text-tinta-fraca">Carregando a proposta…</p>;
  if (q.isError) return <Vazio titulo="Não foi possível carregar a proposta" dica={(q.error as FalhaApi).message} />;
  const linhas = q.data.linhas;
  const enviadas = linhas.filter((l) => l.situacao === "enviada");
  const alternar = (id: string) => setAbertas((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <Painel className="mt-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h2 className="font-display text-[16px] font-bold">{area.centro_custo_codigo} · {area.centro_custo_nome}</h2>
        <span className={`rounded px-1.5 py-0.5 text-[11.5px] font-semibold ${COR_STATUS_AREA[area.status]}`}>{ROTULO_STATUS_AREA[area.status]}</span>
        {enviadas.length > 0 && (
          <button type="button" disabled={decidir.isPending} className="ml-auto rounded-lg bg-laranja px-3 py-1.5 text-[12.5px] font-bold text-azul disabled:opacity-45"
            onClick={() => decidir.mutate(enviadas.map((l) => ({ linha_id: l.id, decisao: "aprovar" })))}>
            {enviadas.length === 1 ? "Aprovar a enviada como veio" : `Aprovar as ${enviadas.length} enviadas como vieram`}
          </button>
        )}
      </div>
      {decidir.isError && <p className="mb-2 text-[12.5px] text-risco-tinta">{(decidir.error as FalhaApi).message}</p>}
      <div className="thin-scroll overflow-x-auto">
        <table className="w-full min-w-[920px] text-[12.5px]">
          <thead className="text-left text-[11px] text-tinta-fraca">
            <tr>
              <th className="pb-1.5">Categoria</th><th className="pb-1.5 text-right">{ano - 1}</th><th className="pb-1.5 text-right">Pedido</th>
              <th className="pb-1.5 text-right">vs</th><th className="pb-1.5 text-right">Aprovado</th><th className="pb-1.5 pl-4">O gestor explica</th>
              <th className="pb-1.5">Situação</th><th className="pb-1.5" />
            </tr>
          </thead>
          <tbody>
            {porCategoria(linhas).map((g) => {
              const desenhar = (l: LinhaProposta, rotulo?: ReactNode) => {
                const base = BigInt(l.base_total_centavos), pedido = BigInt(l.pedido_total_centavos);
                return (
                  <LinhaDetalhe key={l.id} l={l} base={base} pedido={pedido} rotulo={rotulo} aberta={abertas.has(l.id)} onAlternar={() => alternar(l.id)}
                    edicao={edicao?.linha.id === l.id ? edicao : null} ocupado={decidir.isPending}
                    onAprovar={() => decidir.mutate([{ linha_id: l.id, decisao: "aprovar" }])}
                    onEditar={(modo) => setEdicao({ linha: l, modo, aprovado: [...l.pedido_centavos], justificativa: "" })}
                    onMudarEdicao={(e) => setEdicao(e)}
                    onConfirmar={(e) => decidir.mutate([{ linha_id: l.id, decisao: e.modo,
                      ...(e.modo === "ajustar" ? { aprovado_centavos: e.aprovado } : {}), justificativa: e.justificativa }])}
                    onCancelar={() => setEdicao(null)} />
                );
              };
              // Sem pessoa nem subcategoria, a categoria continua uma linha só.
              if (g.detalhes.length === 0) return <Fragment key={g.chave}>{[g.categoria, ...g.itens].filter(Boolean).map((l) => desenhar(l!))}</Fragment>;
              const aberto = abertas.has(g.chave);
              const enviadasCat = [g.categoria, ...g.detalhes, ...g.itens].filter((l): l is LinhaProposta => !!l && l.situacao === "enviada");
              return (
                <Fragment key={g.chave}>
                  <TotalCategoria g={g} aberto={aberto} onAlternar={() => alternar(g.chave)} ocupado={decidir.isPending}
                    onAprovarTudo={enviadasCat.length ? () => decidir.mutate(enviadasCat.map((l) => ({ linha_id: l.id, decisao: "aprovar" as const }))) : null} />
                  {aberto && (
                    <>
                      {g.detalhes.map((l) => desenhar(l, <span className="pl-4">↳ {detalheDaLinha(l)}</span>))}
                      {g.categoria && desenhar(g.categoria, <span className="pl-4 italic text-tinta-media">↳ {g.porPessoa ? "Sem pessoa ligada" : "Sem subcategoria"}</span>)}
                      {g.itens.map((l) => desenhar(l))}
                    </>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Painel>
  );
}

/** As linhas de uma área por categoria: a linha dela, as pessoas ou subcategorias (082), os itens novos. */
type GrupoCategoria = {
  chave: string;
  nome: string;
  categoria: LinhaProposta | null;
  detalhes: LinhaProposta[];
  itens: LinhaProposta[];
  porPessoa: boolean;
};
function porCategoria(linhas: LinhaProposta[]): GrupoCategoria[] {
  const m = new Map<string, GrupoCategoria>();
  for (const l of linhas) {
    const g = m.get(l.categoria_id) ?? {
      chave: `cat-${l.categoria_id}`, nome: `${l.categoria_codigo ? `${l.categoria_codigo} ` : ""}${l.categoria_nome}`,
      categoria: null, detalhes: [], itens: [], porPessoa: false,
    };
    if (l.descricao) g.itens.push(l);
    else if (detalheDaLinha(l)) { g.detalhes.push(l); if (l.fornecedor_id) g.porPessoa = true; }
    else g.categoria = l;
    m.set(l.categoria_id, g);
  }
  for (const g of m.values()) g.detalhes.sort((a, b) => (detalheDaLinha(a) ?? "").localeCompare(detalheDaLinha(b) ?? ""));
  return [...m.values()];
}

/** O total de uma categoria com detalhe: soma as linhas dela, e abre o detalhe. */
function TotalCategoria({ g, aberto, onAlternar, ocupado, onAprovarTudo }: {
  g: GrupoCategoria;
  aberto: boolean;
  onAlternar: () => void;
  ocupado: boolean;
  onAprovarTudo: (() => void) | null;
}) {
  const ls = [g.categoria, ...g.detalhes, ...g.itens].filter((l): l is LinhaProposta => !!l);
  const base = ls.reduce((t, l) => t + BigInt(l.base_total_centavos), 0n);
  const pedido = ls.reduce((t, l) => t + BigInt(l.pedido_total_centavos), 0n);
  const aprovados = ls.filter((l) => l.aprovado_total_centavos !== null);
  const aprovado = aprovados.reduce((t, l) => t + BigInt(l.aprovado_total_centavos!), 0n);
  const contagem = (["enviada", "devolvida", "rascunho"] as const).map((st) => [st, ls.filter((l) => l.situacao === st).length] as const)
    .filter(([, n]) => n > 0).map(([st, n]) => `${n} ${ROTULO_SITUACAO[st].toLowerCase()}${n > 1 ? "s" : ""}`);
  const quantos = g.detalhes.length;
  return (
    <tr className="border-t border-linha align-top">
      <td className="py-1.5 pr-2">
        <button type="button" onClick={onAlternar} aria-expanded={aberto} className="text-left hover:underline">
          <span aria-hidden className="text-tinta-fraca">{aberto ? "▾ " : "▸ "}</span><b>{g.nome}</b>
          <span className="ml-1 text-[11px] text-tinta-fraca">
            ({quantos} {g.porPessoa ? (quantos === 1 ? "pessoa" : "pessoas") : (quantos === 1 ? "subcategoria" : "subcategorias")})
          </span>
        </button>
      </td>
      <td className="num py-1.5 text-right">{reais(base)}</td>
      <td className="num py-1.5 text-right font-semibold">{reais(pedido)}</td>
      <td className="num py-1.5 text-right"><Variacao base={base} pedido={pedido} /></td>
      <td className="num py-1.5 text-right">{aprovados.length ? reais(aprovado) : "—"}</td>
      <td className="max-w-[260px] py-1.5 pr-4 pl-4 text-tinta-media">{g.categoria?.observacao ?? ""}</td>
      <td className="py-1.5 text-[11.5px] whitespace-nowrap text-tinta-media">
        {contagem.length ? contagem.join(" · ") : aprovados.length === ls.length ? "Aprovada" : ""}
      </td>
      <td className="py-1.5 whitespace-nowrap">
        {onAprovarTudo && (
          <button type="button" disabled={ocupado} onClick={onAprovarTudo}
            className="rounded border border-linha bg-superficie px-2 py-0.5 text-[11.5px] font-semibold hover:border-laranja disabled:opacity-50">
            Aprovar a categoria
          </button>
        )}
      </td>
    </tr>
  );
}

function LinhaDetalhe({ l, base, pedido, rotulo, aberta, onAlternar, edicao, ocupado, onAprovar, onEditar, onMudarEdicao, onConfirmar, onCancelar }: {
  l: LinhaProposta;
  base: bigint;
  pedido: bigint;
  /** O nome da linha dentro da categoria aberta (a pessoa, a subcategoria, o resto). */
  rotulo?: ReactNode;
  aberta: boolean;
  onAlternar: () => void;
  edicao: Edicao | null;
  ocupado: boolean;
  onAprovar: () => void;
  onEditar: (modo: "ajustar" | "devolver") => void;
  onMudarEdicao: (e: Edicao) => void;
  onConfirmar: (e: Edicao) => void;
  onCancelar: () => void;
}) {
  const BOTAO = "rounded border border-linha bg-superficie px-2 py-0.5 text-[11.5px] font-semibold hover:border-laranja disabled:opacity-50";
  const cor = l.situacao === "devolvida" ? "text-risco-tinta" : l.situacao === "enviada" ? "text-atencao-tinta" : l.situacao === "rascunho" ? "text-tinta-fraca" : "text-ok-tinta";
  return (
    <>
      <tr className="border-t border-linha align-top">
        <td className="py-1.5 pr-2">
          <button type="button" onClick={onAlternar} className="text-left hover:underline" title="Ver mês a mês">
            {rotulo ?? (l.descricao ? <span>↳ {l.descricao} <span className="text-tinta-fraca">({l.categoria_nome})</span></span>
              : <b>{l.categoria_codigo ? `${l.categoria_codigo} ` : ""}{l.categoria_nome}</b>)}
          </button>
          {l.meses_acima.length > 0 && !l.descricao && <span className="block text-[11px] text-atencao-tinta">acima em {textoMesesAcima(l.meses_acima)}</span>}
        </td>
        <td className="num py-1.5 text-right">{reais(base)}</td>
        <td className="num py-1.5 text-right font-semibold">{reais(pedido)}</td>
        <td className="num py-1.5 text-right"><Variacao base={base} pedido={pedido} /></td>
        <td className="num py-1.5 text-right">{l.aprovado_total_centavos ? reais(l.aprovado_total_centavos) : "—"}</td>
        <td className="max-w-[260px] py-1.5 pr-4 pl-4 text-tinta-media">{l.observacao ?? ""}</td>
        <td className={`py-1.5 whitespace-nowrap ${cor}`}>
          {ROTULO_SITUACAO[l.situacao]}
          {l.justificativa && (l.situacao === "ajustada" || l.situacao === "devolvida") &&
            <span className="block max-w-[220px] text-[11px] whitespace-normal text-tinta-fraca" title={l.justificativa}>{l.justificativa}</span>}
        </td>
        <td className="py-1.5 whitespace-nowrap">
          {l.situacao === "enviada" && !edicao && (
            <span className="flex gap-1">
              <button type="button" disabled={ocupado} onClick={onAprovar} className={BOTAO}>Aprovar</button>
              <button type="button" disabled={ocupado} onClick={() => onEditar("ajustar")} className={BOTAO}>Ajustar</button>
              <button type="button" disabled={ocupado} onClick={() => onEditar("devolver")} className={BOTAO}>Devolver</button>
            </span>
          )}
        </td>
      </tr>
      {(aberta || edicao) && (
        <tr>
          <td colSpan={8} className="pb-3">
            <table className="text-[11.5px]">
              <thead className="text-tinta-fraca">
                <tr><th />{MESES.map((m) => <th key={m} className="px-1 text-right font-normal">{m}</th>)}<th className="px-2 text-right font-normal">Total</th></tr>
              </thead>
              <tbody>
                <tr className="text-tinta-fraca"><td className="pr-2">{l.ano_base}</td>{l.base_centavos.map((v, i) => <td key={i} className="num px-1 text-right">{curto(v)}</td>)}<td className="num px-2 text-right">{curto(soma(l.base_centavos).toString())}</td></tr>
                <tr><td className="pr-2 font-semibold">Pedido</td>{l.pedido_centavos.map((v, i) => (
                  <td key={i} className={`num px-1 text-right ${l.meses_acima.includes(i + 1) ? "bg-atencao-bg" : ""}`}>{curto(v)}</td>))}
                  <td className="num px-2 text-right font-semibold">{curto(soma(l.pedido_centavos).toString())}</td></tr>
                {l.aprovado_centavos && <tr className="text-ok-tinta"><td className="pr-2 font-semibold">Aprovado</td>{l.aprovado_centavos.map((v, i) => <td key={i} className="num px-1 text-right">{curto(v)}</td>)}<td className="num px-2 text-right">{curto(soma(l.aprovado_centavos).toString())}</td></tr>}
                {edicao?.modo === "ajustar" && (
                  <tr><td className="pr-2 font-semibold text-azul-medio">Aprovar com</td>{edicao.aprovado.map((v, i) => (
                    <td key={i} className="px-0.5"><Celula valor={v} onMudar={(c) => onMudarEdicao({ ...edicao, aprovado: edicao.aprovado.map((x, j) => (j === i ? c : x)) })} /></td>))}
                    <td className="num px-2 text-right font-semibold">{curto(soma(edicao.aprovado).toString())}</td></tr>
                )}
              </tbody>
            </table>
            {edicao && (
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <label className="text-[11.5px] font-semibold text-tinta-media">
                  {edicao.modo === "ajustar" ? "Por que o ajuste? O gestor vai ver." : "Por que devolve? O gestor vai ver."}
                  <input value={edicao.justificativa} onChange={(e) => onMudarEdicao({ ...edicao, justificativa: e.target.value })}
                    className="mt-0.5 block w-[420px] max-w-full rounded border border-linha bg-superficie px-2 py-1 font-normal" />
                </label>
                <button type="button" disabled={!edicao.justificativa.trim() || ocupado} onClick={() => onConfirmar(edicao)}
                  className="rounded-lg bg-laranja px-3 py-1 text-[12px] font-bold text-azul disabled:opacity-45">
                  {edicao.modo === "ajustar" ? "Aprovar com este valor" : "Devolver ao gestor"}
                </button>
                <button type="button" onClick={onCancelar} className={CLASSE_BOTAO_BARRA}>Cancelar</button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

type Somas = { base: bigint; pedido: bigint; aprovado: bigint };
/** Todas as áreas somadas, por categoria: o impacto no total. A categoria abre nas pessoas, subcategorias e itens (082), por área. */
function Consolidado({ ano, codigoDe }: { ano: number; codigoDe: Map<string, string> }) {
  const q = useQuery({ queryKey: ["propostas-linhas", ano], queryFn: () => buscarLinhasPropostas(ano) });
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const grupos = useMemo(() => {
    const m = new Map<string, { chave: string; nome: string; areas: Set<string>; detalhes: Map<string, Somas & { rotulo: string }> } & Somas>();
    for (const l of q.data?.linhas ?? []) {
      const g = m.get(l.categoria_id) ?? { chave: l.categoria_id, nome: `${l.categoria_codigo ? `${l.categoria_codigo} ` : ""}${l.categoria_nome}`,
        areas: new Set(), detalhes: new Map(), base: 0n, pedido: 0n, aprovado: 0n };
      const b = BigInt(l.base_total_centavos), pe = BigInt(l.pedido_total_centavos), ap = BigInt(l.aprovado_total_centavos ?? "0");
      g.areas.add(l.centro_custo_id);
      g.base += b; g.pedido += pe; g.aprovado += ap;
      const nome = l.descricao ?? detalheDaLinha(l);
      const k = `${l.centro_custo_id}|${nome ?? ""}`;
      const d = g.detalhes.get(k) ?? { rotulo: `${nome ?? "Sem pessoa ou subcategoria"} · ${codigoDe.get(l.centro_custo_id) ?? ""}`, base: 0n, pedido: 0n, aprovado: 0n };
      d.base += b; d.pedido += pe; d.aprovado += ap;
      g.detalhes.set(k, d);
      m.set(l.categoria_id, g);
    }
    // Só abre a categoria que tem pessoa, subcategoria ou item: a linha dela
    // sozinha seria o próprio total repetido. A que abre mostra também o que
    // não é de ninguém, para as linhas somarem o total.
    for (const g of m.values()) {
      const temDetalhe = [...g.detalhes.keys()].some((k) => !k.endsWith("|"));
      for (const [k, d] of g.detalhes) {
        if (k.endsWith("|") && (!temDetalhe || (d.base === 0n && d.pedido === 0n && d.aprovado === 0n))) g.detalhes.delete(k);
      }
    }
    return [...m.values()].sort((a, b) => (b.pedido > a.pedido ? 1 : b.pedido < a.pedido ? -1 : 0));
  }, [q.data, codigoDe]);
  if (q.isPending) return <p className="text-[13px] text-tinta-fraca">Carregando…</p>;
  if (q.isError) return <Vazio titulo="Não foi possível carregar" dica={(q.error as FalhaApi).message} />;
  if (grupos.length === 0) return <Painel><Vazio titulo="Nenhuma área começou a proposta ainda" /></Painel>;
  const tot = grupos.reduce((s, g) => ({ base: s.base + g.base, pedido: s.pedido + g.pedido, aprovado: s.aprovado + g.aprovado }), { base: 0n, pedido: 0n, aprovado: 0n });
  return (
    <CaixaGrade>
      <table className="w-full border-separate border-spacing-0 text-[12.5px]">
        <thead className="sticky top-0 z-10 bg-superficie-2 text-left text-[11px] font-semibold tracking-wide text-tinta-fraca">
          <tr>
            {["Categoria", "Áreas", `${ano - 1}`, `Pedido ${ano}`, "vs", "Aprovado"].map((h, i) => (
              <th key={h} className={`border-b border-linha px-3 py-2 ${i >= 1 ? "text-right" : ""}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grupos.map((g) => {
            const aberto = abertas.has(g.chave);
            const detalhes = [...g.detalhes.values()].sort((a, b) => a.rotulo.localeCompare(b.rotulo));
            return (
              <Fragment key={g.chave}>
                <tr>
                  <td className="border-b border-linha px-3 py-1.5">
                    {detalhes.length > 0 ? (
                      <button type="button" aria-expanded={aberto} className="text-left hover:underline"
                        onClick={() => setAbertas((s) => { const n = new Set(s); if (n.has(g.chave)) n.delete(g.chave); else n.add(g.chave); return n; })}>
                        <span aria-hidden className="text-tinta-fraca">{aberto ? "▾ " : "▸ "}</span>{g.nome}
                      </button>
                    ) : g.nome}
                  </td>
                  <td className="num border-b border-linha px-3 py-1.5 text-right">{g.areas.size}</td>
                  <td className="num border-b border-linha px-3 py-1.5 text-right">{reais(g.base)}</td>
                  <td className="num border-b border-linha px-3 py-1.5 text-right font-semibold">{reais(g.pedido)}</td>
                  <td className="num border-b border-linha px-3 py-1.5 text-right"><Variacao base={g.base} pedido={g.pedido} /></td>
                  <td className="num border-b border-linha px-3 py-1.5 text-right">{reais(g.aprovado)}</td>
                </tr>
                {aberto && detalhes.map((d) => (
                  <tr key={d.rotulo} className="text-[12px] text-tinta-media">
                    <td className="border-b border-linha/60 py-1 pr-3 pl-9">↳ {d.rotulo}</td>
                    <td className="border-b border-linha/60" />
                    <td className="num border-b border-linha/60 px-3 py-1 text-right">{reais(d.base)}</td>
                    <td className="num border-b border-linha/60 px-3 py-1 text-right">{reais(d.pedido)}</td>
                    <td className="num border-b border-linha/60 px-3 py-1 text-right"><Variacao base={d.base} pedido={d.pedido} /></td>
                    <td className="num border-b border-linha/60 px-3 py-1 text-right">{reais(d.aprovado)}</td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot className="sticky bottom-0 bg-superficie-2 font-semibold">
          <tr>
            <td className="px-3 py-2" colSpan={2}>Total das áreas</td>
            <td className="num px-3 py-2 text-right">{reais(tot.base)}</td>
            <td className="num px-3 py-2 text-right">{reais(tot.pedido)}</td>
            <td className="num px-3 py-2 text-right"><Variacao base={tot.base} pedido={tot.pedido} /></td>
            <td className="num px-3 py-2 text-right">{reais(tot.aprovado)}</td>
          </tr>
        </tfoot>
      </table>
    </CaixaGrade>
  );
}
